import i18next from "i18next";
import { UsersRound } from "lucide-react";
import { lazy, Suspense, type ReactNode } from "react";
import type { PluginUI } from "../../../src/plugin-ui-contract";
const Overview = lazy(() => import("./Overview"));
const ReportsCentre = lazy(() => import("./ReportsCentre"));
const Reports = lazy(() => import("./Reports"));
const Workspace = lazy(() => import("./Workspace"));
const SelfService = lazy(() => import("./SelfService"));
const Timesheets = lazy(() => import("./Timesheets"));
const DutyRoster = lazy(() => import("./DutyRoster"));
const Attendance = lazy(() => import("./Attendance"));
const Employees = lazy(() => import("./Employees"));
const Assignments = lazy(() =>
  import("./People").then((m) => ({ default: m.Assignments })),
);
const LeavePage = lazy(() =>
  import("./People").then((m) => ({ default: m.LeavePage })),
);
const PeopleSettings = lazy(() =>
  import("./People").then((m) => ({ default: m.PeopleSettings })),
);
// Keep lazy module loading inside the content area instead of hiding AppRouter.
function PeopleRoute({ children }: { children: ReactNode }) {
  // The host initializes its UI surface after importing plugin manifests.
  const { Loading } = (window as unknown as { CARE_UI: PluginUI }).CARE_UI;
  return (
    <Suspense
      fallback={
        <div className="px-3 md:px-6" data-people-route-loading>
          <Loading />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const LeavePolicies = lazy(() =>
  import("./LeavePolicies").then((m) => ({ default: m.LeavePolicies })),
);
const LeaveBalances = lazy(() =>
  import("./LeavePolicies").then((m) => ({ default: m.LeaveBalances })),
);

const TeamAvailability = lazy(() =>
  import("./Workforce").then((m) => ({ default: m.TeamAvailability })),
);
interface FacilityProps {
  facilityId: string;
}
export default {
  plugin: "macare_hrms_fe",
  routes: {
    "/facility/:facilityId/hrms/reports/:reportId": ({
      facilityId,
      reportId,
    }: {
      facilityId: string;
      reportId: string;
    }) => (
      <PeopleRoute>
        <Reports
          key={`${facilityId}/${reportId}`}
          facilityId={facilityId}
          reportId={reportId}
        />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/reports": ({
      facilityId,
    }: {
      facilityId: string;
    }) => (
      <PeopleRoute>
        <ReportsCentre key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/workspace": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <Workspace key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/approvals": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <SelfService key={facilityId} facilityId={facilityId} inbox />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/timesheets": ({
      facilityId,
    }: FacilityProps) => (
      <PeopleRoute>
        <Timesheets key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/roster": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <DutyRoster key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/attendance": ({
      facilityId,
    }: FacilityProps) => (
      <PeopleRoute>
        <Attendance key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/team": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <TeamAvailability key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/policies": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <LeavePolicies key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/balances": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <LeaveBalances key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/assignments": ({
      facilityId,
    }: FacilityProps) => (
      <PeopleRoute>
        <Assignments key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/settings": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <PeopleSettings key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/leave/:leaveId": ({
      facilityId,
      leaveId,
    }: FacilityProps & { leaveId: string }) => (
      <PeopleRoute>
        <LeavePage
          key={`${facilityId}/${leaveId}`}
          facilityId={facilityId}
          leaveId={leaveId}
        />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/leave": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <LeavePage key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/employees/:employeeId": ({
      facilityId,
      employeeId,
    }: FacilityProps & { employeeId: string }) => (
      <PeopleRoute>
        <Employees
          key={`${facilityId}/${employeeId}`}
          facilityId={facilityId}
          employeeId={employeeId}
        />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/employees": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <Employees key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms": ({ facilityId }: FacilityProps) => (
      <PeopleRoute>
        <Employees key={facilityId} facilityId={facilityId} />
      </PeopleRoute>
    ),
    "/facility/:facilityId/hrms/:employeeId": ({
      facilityId,
      employeeId,
    }: FacilityProps & { employeeId: string }) => (
      <PeopleRoute>
        <Employees
          key={`${facilityId}/${employeeId}`}
          facilityId={facilityId}
          employeeId={employeeId}
        />
      </PeopleRoute>
    ),
  },
  navItems: [
    {
      name: i18next.t("people_nav_people", "People") || "People",
      url: "hrms",
      icon: <UsersRound />,
      children: [
        {
          name: i18next.t("people_workspace", "My workspace") || "My workspace",
          url: "hrms/workspace",
        },
        {
          name:
            i18next.t("people_approvals", "Approval inbox") || "Approval inbox",
          url: "hrms/approvals",
        },
        {
          name: i18next.t("people_timesheets", "Timesheets") || "Timesheets",
          url: "hrms/timesheets",
        },
        {
          name: i18next.t("people_duty_title", "Duty roster") || "Duty roster",
          url: "hrms/roster",
        },
        {
          name: i18next.t("people_att_title", "Attendance") || "Attendance",
          url: "hrms/attendance",
        },
        {
          name: i18next.t("people_nav_employees", "Employees") || "Employees",
          url: "hrms/employees",
        },
        {
          name:
            i18next.t("people_nav_work_assignments", "Work assignments") ||
            "Work assignments",
          url: "hrms/assignments",
        },
        {
          name:
            i18next.t("people_team_availability", "Team availability") ||
            "Team availability",
          url: "hrms/team",
        },
        {
          name: i18next.t("people_nav_leave", "Leave") || "Leave",
          url: "hrms/leave",
        },
        {
          name:
            i18next.t("people_leave_balances", "Leave balances") ||
            "Leave balances",
          url: "hrms/balances",
        },
        {
          name: i18next.t("reports", "Reports") || "Reports",
          url: "hrms/reports",
        },
        {
          name:
            i18next.t("people_leave_policies", "Leave policies") ||
            "Leave policies",
          url: "hrms/policies",
        },
        {
          name:
            i18next.t("people_nav_people_settings", "People settings") ||
            "People settings",
          url: "hrms/settings",
        },
      ],
    },
  ],
  extends: [],
  components: { FacilityOverviewSections: Overview },
  devices: [],
};
